/* eslint-disable jira/filename-case */
import type { KeySequence } from './types.tsx';

/**
 * Converts a KeyboardEvent to a normalised string representation of
 * modifiers and key.
 *
 * Ordering of modifier keys is taken from Apple's guidelines:
 * https://developer.apple.com/macos/human-interface-guidelines/input-and-output/keyboard/
 *
 * Example representation of ArrowUp key with all modifiers present:
 * ctrl+alt+shift+meta+arrowup
 *
 * Character keys never include the 'shift' modifier,.
 * as it changes the character itself.
 * Examples:
 *  - instead of 'shift+a' the normal form is 'A'
 *  - instead of 'shift+/' the normal form is '?'
 *
 * The 'alt' modifier is now supported in modern browsers by using event.code
 * to extract the base character when alt modifies the character output.
 * On Mac, alt changes the character input (e.g. 'alt+z' displays 'Ω'),
 * but we extract the base character 'z' for consistent keyboard shortcuts.
 * For older browsers without event.code support, alt shortcuts will still
 * have the original limitation where modified characters are used.
 */

// Special keys such as 'Escape', 'ArrowUp' or ' '
const isNonCharacterKey = (e: KeyboardEvent) => e.key.length > 1 || e.key === ' ';

// Base modifiers with alt support
const baseModifiers = [
	{
		name: 'ctrl',
		test: (e: KeyboardEvent) => e.ctrlKey,
	},
	{
		name: 'alt',
		test: (e: KeyboardEvent) => e.altKey,
	},
	{
		name: 'shift',
		test: (e: KeyboardEvent) => e.shiftKey && isNonCharacterKey(e),
	},
	{
		// Command key on MacOS, Windows key on Windows
		name: 'meta',
		test: (e: KeyboardEvent) => e.metaKey || (e.getModifierState && e.getModifierState('OS')),
	},
];

const toKey = (e: KeyboardEvent) => {
	if (isNonCharacterKey(e)) {
		return e.key.toLowerCase();
	}

	// Handle alt-modified characters by using the base character
	// On Mac, alt+z produces 'ω' but we want 'z' for consistent shortcuts
	if (e.altKey && e.code && e.code.startsWith('Key')) {
		// Extract the base character from the code (e.g., 'KeyZ' -> 'z')
		const baseChar = e.code.substring(3).toLowerCase();
		return e.shiftKey ? baseChar.toUpperCase() : baseChar;
	}

	// Ignore accidental CapsLock
	return e.shiftKey ? e.key.toUpperCase() : e.key.toLowerCase();
};

export const toKeyWithModifiers = (e: KeyboardEvent): KeySequence => {
	if (!e || !e.key) {
		// There are still browsers out there that do not support e.key;
		// Just today (Oct 2018) I have observed Chrome 47 in Sentry, but it started supporting e.key since 51.
		// So instead of crashing the page with TypeError let's not detect keyboard shortcuts.
		return {
			sequence: '',
			e,
		};
	}

	return {
		sequence: baseModifiers
			.filter((modifier) => modifier.test(e))
			.map((modifier) => modifier.name)
			.concat(toKey(e))
			.join('+'),
		e,
	};
};

/*
 *  Selected special key values disjunction for regex. All lowercase, ordered alphabethically.
 *  See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values.
 */
const orSpecialChars =
	' |arrowdown|arrowleft|arrowright|arrowup|backspace|clear|delete|end|enter|escape|home|insert|pagedown|pageup|pause|printscreen|tab';

/*
 * This string when used as a regular expression:
 * 1. Allows lowercase only with a single exception (see 3)
 * 2. Allows optional ctrl, shift, meta followed by "+" in that specific order in the beginning
 * 3. Allows any printable character (may be uppercase letter) or a special key value
 * 4. If shift modifier is used it can only be followed by a special char
 * 5. Is based on toKeyWithModifiers, uses orSpecialChars whitelist for shift chars
 * 6. Does not capture redundant groups
 */
const singleShortcutEntry = `(ctrl\\+)?(alt\\+)?(shift\\+(?=(?:(?:meta\\+)?(?:${orSpecialChars}))))?(meta\\+)?(${orSpecialChars}|[!-~])`;

/*
 * Any number of singleShortcutEntry separated by a single space
 * We are not specifying "g" flag, so it's fine to reuse the same regex object.
 */
const shortcutRegex = RegExp(`^${singleShortcutEntry}( ${singleShortcutEntry})*$`);
export const isValidShortcut = (shortcut: string): boolean => shortcutRegex.test(shortcut);
/**
 * Checks for a specific type of overlap between two shortcuts, where one may be a prefix of the other, separated by a space, to ensure they are distinct commands.
 */
export const isShortcutCollision = (leftShortcut: string, rightShortcut: string): boolean => {
	if (!leftShortcut || !rightShortcut) {
		return leftShortcut === rightShortcut;
	}
	if (rightShortcut === leftShortcut) {
		return true;
	}
	if (leftShortcut.indexOf(rightShortcut) === 0) {
		return leftShortcut[rightShortcut.length] === ' ';
	}
	if (rightShortcut.indexOf(leftShortcut) === 0) {
		return rightShortcut[leftShortcut.length] === ' ';
	}
	return false;
};
